import { useCallback, useEffect, useReducer } from "react";

import { getImageImportStatus, startImageImport } from "./imageImportApi";

const POLL_INTERVAL_MS = 3000;
// Tolerate transient network blips before surfacing a hard failure. We never
// time out on PENDING/STARTED — a queued job stays queued until the worker
// picks it up — only on a real FAILURE or a status endpoint that keeps failing.
const NETWORK_ERROR_TOLERANCE = 3;

const INITIAL_STATE = {
  phase: "idle", // idle | starting | running | success | failure
  taskId: null,
  lifecycle: null, // raw backend state while running: PENDING | STARTED | PROGRESS
  progress: null,
  summary: null,
  error: null,
  errorTicks: 0,
};

const messageFromError = (err) =>
  err?.response?.data?.message || err?.message || "Something went wrong.";

const reducer = (state, action) => {
  switch (action.type) {
    case "start":
      return { ...INITIAL_STATE, phase: "starting" };
    case "started":
      return { ...state, phase: "running", taskId: action.taskId };
    case "lifecycle": // PENDING / STARTED — still waiting, keep polling
      return { ...state, phase: "running", lifecycle: action.state, errorTicks: 0 };
    case "progress":
      return {
        ...state,
        phase: "running",
        lifecycle: "PROGRESS",
        progress: action.progress,
        errorTicks: 0,
      };
    case "pollError": {
      const errorTicks = state.errorTicks + 1;
      if (errorTicks >= NETWORK_ERROR_TOLERANCE) {
        return { ...state, phase: "failure", error: action.error };
      }
      return { ...state, errorTicks };
    }
    case "success":
      return { ...state, phase: "success", summary: action.summary };
    case "failure":
      return { ...state, phase: "failure", error: action.error };
    case "reset":
      return INITIAL_STATE;
    default:
      return state;
  }
};

/**
 * Drives the bulk image-import job: starts it, then polls every 3s until a
 * terminal state. Queued/starting jobs are NOT timed out — only SUCCESS,
 * FAILURE, or a persistently failing status request stop the poll. The poll
 * interval cleans itself up on unmount and the moment the job is no longer
 * running.
 */
export const useImageImport = () => {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const { phase, taskId } = state;

  const start = useCallback(async (overwrite) => {
    dispatch({ type: "start" });
    try {
      const { task_id: id } = await startImageImport(overwrite);
      if (!id) {
        dispatch({ type: "failure", error: "No task id was returned." });
        return;
      }
      dispatch({ type: "started", taskId: id });
    } catch (err) {
      dispatch({ type: "failure", error: messageFromError(err) });
    }
  }, []);

  const reset = useCallback(() => dispatch({ type: "reset" }), []);

  useEffect(() => {
    if (phase !== "running" || !taskId) return undefined;

    let cancelled = false;
    const tick = async () => {
      try {
        const res = await getImageImportStatus(taskId);
        if (cancelled) return;
        switch (res.state) {
          case "PROGRESS":
            dispatch({ type: "progress", progress: res.progress });
            break;
          case "SUCCESS":
            dispatch({ type: "success", summary: res.summary });
            break;
          case "FAILURE":
            dispatch({ type: "failure", error: res.error || "Import failed." });
            break;
          case "STARTED":
            dispatch({ type: "lifecycle", state: "STARTED" });
            break;
          case "PENDING":
          default:
            dispatch({ type: "lifecycle", state: "PENDING" });
            break;
        }
      } catch (err) {
        if (!cancelled) dispatch({ type: "pollError", error: messageFromError(err) });
      }
    };

    tick(); // poll immediately, then on an interval
    const intervalId = setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [phase, taskId]);

  return {
    phase: state.phase,
    lifecycle: state.lifecycle,
    progress: state.progress,
    summary: state.summary,
    error: state.error,
    start,
    reset,
  };
};
